import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Spinner,
} from '../../../shared/components/ui';
import { DocumentFactsEditor } from './DocumentFactsEditor';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { vocabularyApi } from '../../../shared/services/vocabularyApi';

/**
 * DocumentFactsReview - Container component for reviewing all document facts
 *
 * This component:
 * - Fetches vocabulary definitions
 * - Fetches extracted facts for each document
 * - Allows editing facts with vocabulary constraints
 * - Saves changes per document
 */
export function DocumentFactsReview({
  estimateId,
  documents,
  onComplete,
  onBack,
}) {
  // State
  const [vocabulary, setVocabulary] = useState(null);
  const [documentFacts, setDocumentFacts] = useState({});
  const [changedDocuments, setChangedDocuments] = useState(new Set());
  const [savingDocuments, setSavingDocuments] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch vocabulary on mount
  useEffect(() => {
    async function fetchVocabulary() {
      try {
        const vocab = await vocabularyApi.getVocabulary();
        setVocabulary(vocab);
      } catch (err) {
        console.error('Failed to fetch vocabulary:', err);
        // Don't set error - we can still show documents without vocabulary
      }
    }
    fetchVocabulary();
  }, []);

  // Fetch facts for each document
  useEffect(() => {
    async function fetchAllFacts() {
      if (!estimateId || !documents || documents.length === 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const factsMap = {};

        for (const doc of documents) {
          const documentId = doc.documentId || doc.id;
          if (!documentId) continue;

          try {
            const facts = await estimatesApi.getDocumentFacts(estimateId, documentId);
            factsMap[documentId] = facts;
          } catch (err) {
            console.error(`Failed to fetch facts for document ${documentId}:`, err);
            // Store empty facts for this document
            factsMap[documentId] = { divisions: [] };
          }
        }

        setDocumentFacts(factsMap);
      } catch (err) {
        console.error('Failed to fetch document facts:', err);
        setError('Failed to load document information. You can still proceed without reviewing.');
      } finally {
        setIsLoading(false);
      }
    }

    fetchAllFacts();
  }, [estimateId, documents]);

  // Handle fact change
  const handleFactChange = useCallback((documentId, divisionNumber, fieldName, value) => {
    setDocumentFacts((prev) => {
      const docFacts = prev[documentId] || { divisions: [] };
      const divisions = docFacts.divisions.map((div) => {
        if (div.divisionNumber === divisionNumber) {
          return {
            ...div,
            facts: {
              ...div.facts,
              [fieldName]: value,
            },
          };
        }
        return div;
      });

      return {
        ...prev,
        [documentId]: {
          ...docFacts,
          divisions,
        },
      };
    });

    // Mark document as changed
    setChangedDocuments((prev) => new Set([...prev, documentId]));
  }, []);

  // Handle save for a single document
  const handleSaveDocument = useCallback(
    async (documentId) => {
      const docFacts = documentFacts[documentId];
      if (!docFacts || !docFacts.divisions) return;

      setSavingDocuments((prev) => new Set([...prev, documentId]));

      try {
        // Format divisions for API
        const divisionsToUpdate = docFacts.divisions.map((div) => ({
          divisionNumber: div.divisionNumber,
          facts: div.facts,
        }));

        await estimatesApi.updateDocumentFacts(estimateId, documentId, divisionsToUpdate);

        // Remove from changed set
        setChangedDocuments((prev) => {
          const next = new Set(prev);
          next.delete(documentId);
          return next;
        });
      } catch (err) {
        console.error(`Failed to save facts for document ${documentId}:`, err);
        setError(`Failed to save changes. Please try again.`);
      } finally {
        setSavingDocuments((prev) => {
          const next = new Set(prev);
          next.delete(documentId);
          return next;
        });
      }
    },
    [estimateId, documentFacts]
  );

  // Handle save all
  const handleSaveAll = useCallback(async () => {
    for (const documentId of changedDocuments) {
      await handleSaveDocument(documentId);
    }
  }, [changedDocuments, handleSaveDocument]);

  // Render loading state
  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center text-center">
            <Spinner size="lg" />
            <p className="text-gray-600 dark:text-gray-300 mt-4">Loading document information...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render empty state
  if (!documents || documents.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Review Extracted Facts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">No documents to review</p>
          </div>
          <div className="flex justify-end mt-6">
            <Button onClick={onComplete}>Continue</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Review Extracted Facts</CardTitle>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {documents.length} document{documents.length !== 1 ? 's' : ''}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {/* Info Banner */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">Review is optional</p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  We've extracted key information from your documents. You can review and correct
                  any values using the dropdown menus and input fields below, or skip this step entirely.
                </p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Document List */}
          <div className="space-y-4">
            {documents.map((doc) => {
              const documentId = doc.documentId || doc.id;
              const facts = documentFacts[documentId];

              return (
                <DocumentFactsEditor
                  key={documentId}
                  document={doc}
                  divisions={facts?.divisions || []}
                  vocabulary={vocabulary}
                  onFactChange={handleFactChange}
                  onSave={handleSaveDocument}
                  hasChanges={changedDocuments.has(documentId)}
                  isSaving={savingDocuments.has(documentId)}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-3">
          {changedDocuments.size > 0 && (
            <Button
              variant="outline"
              onClick={handleSaveAll}
              disabled={savingDocuments.size > 0}
            >
              {savingDocuments.size > 0 ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                `Save All (${changedDocuments.size})`
              )}
            </Button>
          )}
          <Button onClick={onComplete}>
            {changedDocuments.size > 0 ? 'Continue Without Saving' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DocumentFactsReview;
